import React, { useEffect, useRef, useState } from "react";
import { InputBase } from "@mui/material";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import { FontSize } from "../../Constants/Enums/Size.enums";
import InputWrapper, { inputButtonStyle } from "../input/inputWrapper";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";

/** Pause after the last keystroke before committing a text filter. */
export const DEFAULT_FILTER_DEBOUNCE_MS = 500;

interface FilterInputProps {
  label: string;
  placeholder: string;
  value: string | number;
  onChange: (value: string | number) => void;
  type?: "text" | "number" | "email";
  width?: string;
  /**
   * Delay before `onChange` fires. `true` uses {@link DEFAULT_FILTER_DEBOUNCE_MS}.
   * The field stays responsive via a local draft; the parent is notified once.
   */
  debounce?: boolean | number;
}

export const FilterInput = ({
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  width = "250px",
  debounce,
}: FilterInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const debounceMs =
    debounce === true
      ? DEFAULT_FILTER_DEBOUNCE_MS
      : typeof debounce === "number"
        ? debounce
        : 0;
  const isDebounced = debounceMs > 0;

  const [draft, setDraft] = useState<string | number>(value);
  const lastEmittedRef = useRef<string | number>(value);

  const emitChange = useDebouncedCallback((next: string | number) => {
    if (next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    onChange(next);
  }, debounceMs);

  useEffect(() => {
    setDraft(value);
    lastEmittedRef.current = value;
    emitChange.cancel();
  }, [value, emitChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const actualValue = type === "number" ? Number(newValue) : newValue;
    setDraft(actualValue);

    if (!isDebounced) {
      lastEmittedRef.current = actualValue;
      onChange(actualValue);
      return;
    }

    emitChange(actualValue);
  };

  const displayValue = type === "number" ? String(draft) : draft;

  const getBorderColor = () => {
    if (isFocused) return Colors.TangerineOrange;
    if (isHovered) return Colors.Grey;
    return Colors.LightGrey;
  };

  const getBackgroundColor = () => {
    if (isHovered) return Colors.VeryLightGrey;
    return Colors.White;
  };

  const getIconColor = () => {
    if (isFocused) return Colors.TangerineOrange;
    if (isHovered) return Colors.Black;
    return Colors.Grey;
  };

  return (
    <div style={{ width: width }}>
      <InputWrapper label={label} wrapperStyle={{ width: "100%" }}>
        <InputBase
          placeholder={placeholder}
          value={displayValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          type={type}
          style={{
            ...inputButtonStyle,
            borderColor: getBorderColor(),
            backgroundColor: getBackgroundColor(),
            boxShadow: isFocused
              ? `0 0 0 3px ${Colors.LIGHT_TANGERINE_ORANGE}`
              : isHovered
                ? "0 2px 8px rgba(0, 0, 0, 0.08)"
                : "none",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            paddingLeft: "12px",
          }}
          sx={{
            "& input": {
              padding: 0,
              fontFamily: FontFamily.POPPINS_REGULAR,
              fontSize: FontSize.F14,
            },
            "& input::placeholder": {
              color: Colors.Grey,
              opacity: 0.7,
            },
          }}
          startAdornment={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                marginRight: "8px",
                transition: "all 0.2s ease",
                color: getIconColor(),
              }}
            >
              <path
                d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                fill="currentColor"
              />
            </svg>
          }
        />
      </InputWrapper>
    </div>
  );
};
